import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  Home,
  ShoppingCart,
  Tag,
  Users,
  BarChart3,
  Store,
  Settings,
  LogOut,
  ExternalLink,
  Menu,
  X,
  Loader2,
  Percent,
  Megaphone,
  Palette,
  Truck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AdminTheme from "./AdminTheme";

const NAV = [
  { to: "/admin", label: "Home", icon: Home, end: true },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/settings/shipping", label: "Shipping Center", icon: Truck },
  { to: "/admin/products", label: "Products", icon: Tag },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/discounts", label: "Discounts", icon: Percent },
  { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/admin/themes", label: "Themes", icon: Palette },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/store", label: "Online Store", icon: Store },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const accentTextColor = (hex) => {
  const value = hex.replace("#", "");
  const channels = [0, 2, 4].map(
    (index) => parseInt(value.slice(index, index + 2), 16) / 255,
  );
  const luminance = channels
    .map((channel) =>
      channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
    )
    .reduce(
      (total, channel, index) =>
        total + channel * [0.2126, 0.7152, 0.0722][index],
      0,
    );
  return luminance > 0.18 ? "#09090b" : "#ffffff";
};

const SidebarNav = ({ onNavigate, testPrefix = "admin-sidebar" }) => {
  const { user, logout } = useAuth();
  return (
    <div className="flex flex-col h-full">
      <Link
        to="/admin"
        className="px-3 py-1 font-bold tracking-tight text-[15px]"
        onClick={onNavigate}
      >
        SPICY<span className="text-[#e11d2a]">3D</span> WORLD
        <span className="block text-[11px] font-medium text-zinc-500 tracking-normal">
          Store admin
        </span>
      </Link>
      <nav className="mt-6 space-y-0.5 flex-1">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={`${testPrefix}-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-white text-zinc-950 shadow-sm border border-zinc-200" : "text-zinc-600 hover:bg-zinc-200/60 hover:text-zinc-950 border border-transparent"}`
            }
          >
            <Icon size={17} strokeWidth={1.8} /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-zinc-200 pt-4 space-y-1">
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
            data-testid={`${testPrefix}-view-store-link`}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-zinc-600 hover:bg-zinc-200/60 hover:text-zinc-950"
        >
          <ExternalLink size={16} /> View store
        </a>
        <button
          onClick={logout}
          data-testid={`${testPrefix}-logout-button`}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-zinc-600 hover:bg-zinc-200/60 hover:text-zinc-950"
        >
          <LogOut size={16} /> Log out
        </button>
        <p className="px-3 pt-2 text-[11px] text-zinc-400 truncate">
          {user?.email}
        </p>
      </div>
    </div>
  );
};

export default function AdminLayout() {
  const { user, ensure } = useAuth();
  useEffect(() => {
    ensure();
  }, [ensure]);
  const [open, setOpen] = useState(false);
  const [appearance, setAppearance] = useState(null);
  const loc = useLocation();

  useEffect(() => {
    const load = () =>
      api
        .get("/admin/appearance")
        .then(({ data }) => setAppearance(data))
        .catch(() => {});
    const handleAppearance = (event) => setAppearance(event.detail);
    load();
    window.addEventListener("admin-appearance", handleAppearance);
    return () =>
      window.removeEventListener("admin-appearance", handleAppearance);
  }, []);

  if (user === null)
    return (
      <div className="admin min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-zinc-400" />
      </div>
    );
  if (user === false)
    return (
      <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />
    );

  const appearanceStyle = appearance
    ? {
        "--admin-accent": appearance.accent,
        "--admin-accent-text": accentTextColor(appearance.accent),
        "--admin-intensity": appearance.intensity / 100,
      }
    : { "--admin-accent-text": "#09090b" };

  return (
    <div
      className="admin min-h-screen flex"
      data-testid="admin-layout"
      data-admin-theme={appearance?.theme || "midnight"}
      data-admin-background={appearance?.background || "solid"}
      data-admin-glass={appearance?.glass ? "true" : "false"}
      style={appearanceStyle}
    >
      <AdminTheme />
      <aside className="admin-sidebar hidden lg:block w-60 flex-shrink-0 border-r border-white/10 p-4 h-screen sticky top-0">
        <SidebarNav />
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="admin-sidebar w-64 p-4 h-full shadow-xl">
            <SidebarNav onNavigate={() => setOpen(false)} testPrefix="admin-mobile" />
          </div>
          <button
            className="flex-1 bg-black/30"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="admin-mobilebar lg:hidden sticky top-0 z-40 text-white border-b border-white/10 h-14 flex items-center justify-between px-4">
          <button
            onClick={() => setOpen(true)}
            data-testid="admin-mobile-menu-button"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <span className="font-bold text-sm">
            SPICY<span className="text-[#e11d2a]">3D</span> WORLD
          </span>
          <span className="w-5" />
        </div>
        <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8 pb-28">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
